import { useState, useEffect } from 'react'
import { type RuleInfo, RuleType } from '@/store/setUpInheritorsSlice'
import { formatDate } from '@/utils/date'
import { Body2White } from '@/components/typography'
import { validateRule } from '@/utils/api'
import { MONTH_IN_SECONDS, YEAR_IN_SECONDS } from '@/utils/date'

const RuleRender = ({
  rule,
  color,
  numInheritors,
  showTag = false,
  lastActivity = 0,
  numClaimed = 0,
}: {
  rule: RuleInfo
  numInheritors: number
  color?: string | undefined
  showTag?: boolean
  lastActivity?: number
  numClaimed?: number
}) => {
  const [status, setStatus] = useState('secured')
  const { type, period, timestamp } = rule

  useEffect(() => {
    if (!showTag || !lastActivity) {
      return
    }

    if (rule.type === RuleType.Unknown) {
      return
    }

    let ruleValue = 0
    if (rule.type === RuleType.AfterInactivityPeriod) {
      const numSeconds = period.type === 'month' ? MONTH_IN_SECONDS : YEAR_IN_SECONDS
      ruleValue = period.value * numSeconds
    }
    if (rule.type === RuleType.OnSpecificDate) {
      ruleValue = timestamp || 0
    }

    const isRuleMet = validateRule(rule.type, ruleValue, lastActivity)
    if (isRuleMet) {
      if (numClaimed === 0) {
        setStatus('ruleMet')
        return
      } else {
        setStatus('claimed')
        return
      }
    }
    setStatus('secured')
  }, [lastActivity, rule, showTag])

  const renderTag = () => {
    if (!showTag || !lastActivity) {
      return <></>
    }
    if (status === 'ruleMet') {
      return tag('rule_met', 'warnning')
    }
    if (status === 'claimed') {
      return tag('claimed', 'error')
    }
    return tag('secured', 'success')
  }

  if (type === RuleType.AfterInactivityPeriod) {
    if (status === 'ruleMet' || status === 'claimed') {
      return (
        <Body2White sx={{ color: 'var(--Ebony-Clay-400, #AAB3CF)' }}>
          {highlightGreenText(`After ${period.value} ${period.type}${period.value > 1 ? 's' : ''}  `, color)}
          of on-chain inactivities - The rule was met, {`${numClaimed}/${numInheritors}`} inheritor
          {numInheritors > 1 ? 's' : ''} claimed the inheritance.
          {renderTag()}
        </Body2White>
      )
    }
    return (
      <Body2White sx={{ color: 'var(--Ebony-Clay-400, #AAB3CF)' }}>
        {highlightGreenText(`After ${period.value} ${period.type}${period.value > 1 ? 's' : ''}  `, color)}
        of on-chain inactivities - {highlightGreenText(`${numInheritors} `, 'var(--Ebony-Clay-300, #D2D7E5)')}
        inheritor{numInheritors > 1 ? 's' : ''} are going to inherit your wallet.
        {renderTag()}
      </Body2White>
    )
  }

  if (type === RuleType.OnSpecificDate && timestamp) {
    if (status === 'ruleMet' || status === 'claimed') {
      return (
        <Body2White sx={{ color: 'var(--Ebony-Clay-400, #AAB3CF)' }}>
          {highlightGreenText(`On ${formatDate(timestamp * 1000)}`, color)} - The rule was met,{' '}
          {`${numClaimed}/${numInheritors}`} inheritor
          {numInheritors > 1 ? 's' : ''} claimed the inheritance.
          {renderTag()}
        </Body2White>
      )
    }
    return (
      <Body2White sx={{ color: 'var(--Ebony-Clay-400, #AAB3CF)' }}>
        {highlightGreenText(`On ${formatDate(timestamp * 1000)}`, color)} -{' '}
        {highlightGreenText(`${numInheritors} `, 'var(--Ebony-Clay-300, #D2D7E5)')} inheritor
        {numInheritors > 1 ? 's' : ''} are going to inherit your wallet.
        {renderTag()}
      </Body2White>
    )
  }

  return <></>
}

const highlightGreenText = (text: string, color?: string | undefined) => {
  return <span style={{ color: color || 'var(--Emerald-600, #22C55E)', fontWeight: 'bold' }}>{text}</span>
}

const tag = (name: string, tag: string) => {
  const color =
    tag === 'success'
      ? 'var(--Emerald-700, #16a34a)'
      : tag === 'warnning'
      ? 'var(--Supporting-Koromiko, #FC8313)'
      : 'var(--Blue-Ribbon-600, #5D5DFF)'

  return (
    <span
      style={{
        border: `1px solid ${color}`,
        // background: 'var(--Emerald-700, #16a34a)',
        color,
        fontSize: '12px',
        fontWeight: '400',
        padding: '1px 8px',
        borderRadius: '4px',
        marginLeft: '8px',
        marginRight: '8px',
      }}
    >
      {name}
    </span>
  )
}

export default RuleRender
